(() => {
  const controllerKey = Symbol.for('soulcrystal.sparkLanding.controller');
  document[controllerKey]?.destroy();

  const pageControllers = new Map();
  const documentListeners = [];

  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    return () => target.removeEventListener(type, handler, options);
  };

  const createGallery = (page, gallery) => {
    const slides = [...gallery.querySelectorAll('[data-spark-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-spark-thumb]')];
    const stage = gallery.querySelector('[data-spark-gallery-stage]');
    if (!stage || slides.length < 2 || slides.length !== thumbs.length) return () => {};

    const cleanups = [];
    let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-selected') === 'true'));
    let touchStart = null;

    const selectMedia = (nextIndex, focusThumb = false, scrollThumb = true) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      thumbs.forEach((thumb, index) => {
        const active = index === activeIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
      if (scrollThumb) thumbs[activeIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (focusThumb) thumbs[activeIndex].focus();
      page.dispatchEvent(new CustomEvent('spark:media-change', {
        bubbles: true,
        detail: { index: activeIndex, type: slides[activeIndex].dataset.mediaType },
      }));
    };

    thumbs.forEach((thumb, index) => {
      cleanups.push(listen(thumb, 'click', () => selectMedia(index)));
      cleanups.push(listen(thumb, 'keydown', (event) => {
        const keyOffsets = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
        if (event.key in keyOffsets) {
          event.preventDefault();
          selectMedia(activeIndex + keyOffsets[event.key], true);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          selectMedia(event.key === 'Home' ? 0 : slides.length - 1, true);
        }
      }));
    });

    cleanups.push(listen(stage, 'touchstart', (event) => {
      if (event.touches.length !== 1) return;
      touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true }));
    cleanups.push(listen(stage, 'touchend', (event) => {
      if (!touchStart || event.changedTouches.length !== 1) return;
      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      const deltaY = event.changedTouches[0].clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY)) selectMedia(activeIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true }));
    cleanups.push(listen(stage, 'touchcancel', () => { touchStart = null; }, { passive: true }));

    selectMedia(activeIndex, false, false);
    return () => cleanups.splice(0).forEach((cleanup) => cleanup());
  };

  const createPageController = (page) => {
    const cleanups = [];
    page.querySelectorAll('[data-spark-gallery]').forEach((gallery) => cleanups.push(createGallery(page, gallery)));

    const videoThumb = page.querySelector('[data-spark-thumb][data-spark-index="1"]');
    const galleryStage = page.querySelector('[data-spark-gallery-stage]');
    page.querySelectorAll('[data-spark-video-trigger]').forEach((trigger) => cleanups.push(listen(trigger, 'click', (event) => {
      event.preventDefault();
      videoThumb?.click();
      galleryStage?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      galleryStage?.querySelector('video')?.focus({ preventScroll: true });
    })));

    const accordionItems = [...page.querySelectorAll('.spark-accordion details')];
    accordionItems.forEach((item) => cleanups.push(listen(item, 'toggle', () => {
      if (!item.open) return;
      accordionItems.forEach((otherItem) => {
        if (otherItem !== item) otherItem.removeAttribute('open');
      });
    })));

    const videos = [...page.querySelectorAll('video')];
    videos.forEach((video) => cleanups.push(listen(video, 'play', () => {
      videos.forEach((otherVideo) => {
        if (otherVideo !== video) otherVideo.pause();
      });
    })));

    return () => cleanups.splice(0).forEach((cleanup) => cleanup());
  };

  const initializeWithin = (root) => {
    const pages = root.matches?.('.spark-page') ? [root] : [...root.querySelectorAll?.('.spark-page') ?? []];
    pages.forEach((page) => {
      pageControllers.get(page)?.();
      pageControllers.set(page, createPageController(page));
    });
  };

  const destroyWithin = (root) => {
    [...pageControllers].forEach(([page, destroy]) => {
      if (page === root || root.contains?.(page)) {
        destroy();
        pageControllers.delete(page);
      }
    });
  };

  const onDocument = (type, handler) => documentListeners.push(listen(document, type, handler));
  onDocument('shopify:section:load', (event) => initializeWithin(event.target));
  onDocument('shopify:section:unload', (event) => destroyWithin(event.target));
  onDocument('shopify:section:select', (event) => initializeWithin(event.target));
  onDocument('shopify:block:select', (event) => {
    const item = event.target.closest?.('.spark-accordion details');
    if (item) item.open = true;
  });

  if (document.readyState === 'loading') onDocument('DOMContentLoaded', () => initializeWithin(document));
  else initializeWithin(document);

  document[controllerKey] = {
    destroy() {
      [...pageControllers.values()].forEach((destroy) => destroy());
      pageControllers.clear();
      documentListeners.splice(0).forEach((cleanup) => cleanup());
      delete document[controllerKey];
    },
  };
})();
